import { formatModalityDisplayLabel } from './modalityUtils';

type ModalityRuleLike = {
  modalityIds?: number[] | null;
  modality_ids?: number[] | null;
  isActive?: boolean;
  is_active?: boolean;
};

/** `null` means prefetch failed or is unavailable — skip frontend validation and rely on backend. */
export function hasPrefetchedCrossRuleValidationData<T>(
  rules: T[] | null | undefined
): rules is T[] {
  return rules !== null && rules !== undefined;
}

function normalizeModalityIds(value?: number[] | null): number[] {
  return Array.isArray(value) ? value.filter(id => Number.isInteger(id) && id > 0) : [];
}

export function getOverlappingModalityIds(
  leftModalityIds?: number[] | null,
  rightModalityIds?: number[] | null
): number[] {
  const left = normalizeModalityIds(leftModalityIds);
  const right = normalizeModalityIds(rightModalityIds);

  if (!left.length || !right.length) {
    return [];
  }

  const rightSet = new Set(right);
  return left.filter(id => rightSet.has(id));
}

export function modalitiesOverlap(
  leftModalityIds?: number[] | null,
  rightModalityIds?: number[] | null
): boolean {
  return getOverlappingModalityIds(leftModalityIds, rightModalityIds).length > 0;
}

function isActiveRule(rule: ModalityRuleLike): boolean {
  return Boolean(rule.isActive ?? rule.is_active);
}

function formatModalityLabelList(
  overlapIds: number[],
  modalityOptions: Array<{ id: number; label?: string; value?: string }>
): string {
  if (!overlapIds.length) {
    return 'any modality';
  }

  const labels = overlapIds.map(id => {
    const option = modalityOptions.find(item => item.id === id);
    if (option?.label) {
      return option.label;
    }
    if (option?.value) {
      return formatModalityDisplayLabel(option.value);
    }
    return `Modality ${id}`;
  });

  return labels.length === 1 ? labels[0] : labels.join(', ');
}

export type ModalityConflictAction = 'add' | 'activate';

export function buildRoutingRuleModalityConflictMessage(
  modalityLabel: string,
  action: ModalityConflictAction = 'add'
): string {
  const verb = action === 'activate' ? 'activate' : 'add';
  return `Cannot ${verb} routing rule for ${modalityLabel} because an active study assignment rule already exists for the same modality.`;
}

export function buildStudyAssignmentModalityConflictMessage(
  modalityLabel: string,
  action: ModalityConflictAction = 'add'
): string {
  const verb = action === 'activate' ? 'activate' : 'add';
  return `Cannot ${verb} study assignment rule for ${modalityLabel} because an active routing rule already exists for the same modality.`;
}

type RuleWithModalities = {
  modalities?: Array<{ id: number; name: string }>;
};

export function buildModalityLabelOptions(
  ...sources: Array<RuleWithModalities | RuleWithModalities[] | null | undefined>
): Array<{ id: number; label?: string; value?: string }> {
  const labelById = new Map<number, string>();

  for (const source of sources) {
    let rules: RuleWithModalities[] = [];
    if (Array.isArray(source)) {
      rules = source;
    } else if (source) {
      rules = [source];
    }

    for (const rule of rules) {
      rule.modalities?.forEach(modality => {
        labelById.set(modality.id, formatModalityDisplayLabel(modality.name));
      });
    }
  }

  return [...labelById.entries()].map(([id, label]) => ({ id, label, value: label }));
}

export function findRoutingRuleModalityConflict(
  newModalityIds: number[],
  activeStudyAssignmentRules: ModalityRuleLike[],
  modalityOptions: Array<{ id: number; label?: string; value?: string }>,
  isNewRuleActive: boolean,
  action: ModalityConflictAction = 'add'
): string | null {
  if (!isNewRuleActive) {
    return null;
  }

  for (const rule of activeStudyAssignmentRules) {
    if (!isActiveRule(rule)) {
      continue;
    }

    const otherIds = rule.modalityIds ?? rule.modality_ids ?? [];
    if (!modalitiesOverlap(newModalityIds, otherIds)) {
      continue;
    }

    const overlapIds = getOverlappingModalityIds(newModalityIds, otherIds);
    const label = formatModalityLabelList(overlapIds, modalityOptions);
    return buildRoutingRuleModalityConflictMessage(label, action);
  }

  return null;
}

export function shouldRunModalityConflictCheck(
  isEdit: boolean,
  editingRule: { modalityIds?: number[] | null; isActive?: boolean } | null | undefined,
  form: { modalityIds: number[]; active: boolean }
): boolean {
  if (!form.active) {
    return false;
  }
  if (!isEdit || !editingRule) {
    return true;
  }
  if (form.active !== editingRule.isActive) {
    return true;
  }

  const original = [...(editingRule.modalityIds ?? [])].sort((left, right) => left - right);
  const current = [...form.modalityIds].sort((left, right) => left - right);
  if (original.length !== current.length) {
    return true;
  }
  return !original.every((id, index) => id === current[index]);
}

export function findStudyAssignmentModalityConflict(
  newModalityIds: number[],
  activeRoutingRules: ModalityRuleLike[],
  modalityOptions: Array<{ id: number; label?: string; value?: string }>,
  isNewRuleActive: boolean,
  action: ModalityConflictAction = 'add'
): string | null {
  if (!isNewRuleActive) {
    return null;
  }

  for (const rule of activeRoutingRules) {
    if (!isActiveRule(rule)) {
      continue;
    }

    const otherIds = rule.modalityIds ?? rule.modality_ids ?? [];
    if (!modalitiesOverlap(newModalityIds, otherIds)) {
      continue;
    }

    const overlapIds = getOverlappingModalityIds(newModalityIds, otherIds);
    const label = formatModalityLabelList(overlapIds, modalityOptions);
    return buildStudyAssignmentModalityConflictMessage(label, action);
  }

  return null;
}
